// Sponsorship banner + submission modal (§4).
// Small unobtrusive banner on the game screen. Clicking it opens a form
// that submits to /api/gm/sponsorship-request (public, non-admin).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mail, X, Handshake } from "lucide-react";
import T from "@/components/T";

export default function SponsorshipBanner() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  return (
    <>
      {!minimized && (
        <div data-testid="sponsorship-banner"
          className="fixed bottom-24 sm:bottom-6 right-3 z-40 max-w-xs flex items-center gap-2 shadow-2xl px-3 py-2 rounded-sm"
          style={{
            backgroundColor: "#0f0d08",
            border: "1px solid #B89C3A",
            boxShadow: "0 8px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(184,156,58,0.2)",
          }}>
          <Handshake className="w-4 h-4 text-[#E5C34A] shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A]"><T>Sponsor / Collab</T></div>
            <div className="font-mono text-[9px] text-[#B0ADA5]"><T>Vuoi entrare nell'ombra?</T></div>
          </div>
          <button data-testid="sponsorship-open"
            onClick={() => setOpen(true)}
            className="px-2 py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[9px] tracking-widest uppercase hover:bg-[#B89C3A]/10">
            <T>Scrivici</T>
          </button>
          <button data-testid="sponsorship-minimize"
            onClick={() => setMinimized(true)}
            className="text-[#666] hover:text-[#E2DFD8]" aria-label="minimize">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {open && <SponsorshipModal onClose={() => setOpen(false)} />}
    </>
  );
}

function SponsorshipModal({ onClose }) {
  const [form, setForm] = useState({ name: "", contact: "", kind: "sponsorship", message: "" });
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name.trim() || !form.contact.trim() || !form.message.trim()) {
      toast.error(t("sponsor.fillAll", "Fill all fields"));
      return;
    }
    setBusy(true);
    try {
      await api.sponsorshipSubmit(form);
      toast.success(t("sponsor.sent", "Request sent! We'll contact you soon."));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-3 py-4 overflow-y-auto"
      data-testid="sponsorship-modal">
      <div className="panel w-full max-w-lg space-y-3">
        <div className="flex items-center gap-2 border-b border-[#262626] px-4 py-3">
          <Mail className="w-4 h-4 text-[#E5C34A]" />
          <span className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Collaborazione</span>
          <button data-testid="sponsorship-close" onClick={onClose} className="ml-auto text-[#666] hover:text-[#E2DFD8]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <p className="font-mono text-[10px] text-[#B0ADA5]">
            Proponi una collaborazione, uno sponsor o un partenariato. Ti risponderemo via
            il contatto che indichi qui sotto.
          </p>
          <F label="Nome / Brand" testid="sponsor-name" value={form.name} onChange={v => u("name", v)} />
          <F label="Contatto (email / discord / URL)" testid="sponsor-contact" value={form.contact} onChange={v => u("contact", v)} />
          <div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Tipo</div>
            <select data-testid="sponsor-kind" value={form.kind} onChange={e => u("kind", e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-1.5 text-xs text-[#E2DFD8]">
              <option value="sponsorship">Sponsor</option>
              <option value="collab">Collaborazione creator</option>
              <option value="partnership">Partenariato / brand</option>
              <option value="press">Stampa / interview</option>
              <option value="other">Altro</option>
            </select>
          </div>
          <F label="Messaggio" testid="sponsor-message" textarea value={form.message} onChange={v => u("message", v)} rows={5} />
          <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
            <button onClick={onClose} className="px-3 py-2 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase">Annulla</button>
            <button data-testid="sponsorship-submit" onClick={submit} disabled={busy}
              className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-50">
              {busy ? "Invio…" : "Invia richiesta"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, testid, textarea, rows }) {
  const cls = "w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-1.5 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none";
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      {textarea
        ? <textarea data-testid={testid} value={value} onChange={e => onChange(e.target.value)} rows={rows || 3} className={`${cls} resize-none`} />
        : <input data-testid={testid} value={value} onChange={e => onChange(e.target.value)} className={cls} />}
    </label>
  );
}
